/**
 * Airtable Service - Metadata Storage for Uploaded Files
 * Stores file metadata in Airtable "Content Files" table
 * Replaces IPFS-based metadata storage
 */

import Airtable from 'airtable';

/**
 * Logger utility for Airtable operations
 */
const logger = {
  info: (msg, data = {}) => console.log(`[AIRTABLE] ℹ️  ${msg}`, { ...data, timestamp: new Date().toISOString() }),
  success: (msg, data = {}) => console.log(`[AIRTABLE] ✅ ${msg}`, { ...data, timestamp: new Date().toISOString() }),
  error: (msg, error = {}) => console.error(`[AIRTABLE] ❌ ${msg}`, { error: error.message || error, stack: error.stack, timestamp: new Date().toISOString() }),
  warn: (msg, data = {}) => console.warn(`[AIRTABLE] ⚠️  ${msg}`, { ...data, timestamp: new Date().toISOString() })
};

class AirtableService {
  constructor() {
    const baseId = process.env.REACT_APP_AIRTABLE_BASE_ID;
    const apiKey = process.env.REACT_APP_AIRTABLE_API_KEY;

    logger.info('Initializing Airtable service', {
      hasApiKey: !!apiKey,
      apiKeyLength: apiKey?.length,
      hasBaseId: !!baseId
    });

    if (apiKey && baseId) {
      // Trim whitespace from credentials
      const cleanApiKey = apiKey.trim();
      const cleanBaseId = baseId.trim();

      logger.info('Configuring Airtable', {
        trimmedApiKeyLength: cleanApiKey.length,
        originalApiKeyLength: apiKey.length,
        baseId: cleanBaseId
      });

      try {
        Airtable.configure({
          endpointUrl: 'https://api.airtable.com',
          apiKey: cleanApiKey
        });
        this.base = Airtable.base(cleanBaseId);
        logger.success('Airtable configured successfully', { baseId: cleanBaseId });
      } catch (error) {
        logger.error('Airtable configuration failed', error);
        this.base = null;
      }
    } else {
      logger.warn('Airtable credentials not configured', {
        hasApiKey: !!apiKey,
        hasBaseId: !!baseId
      });
      this.base = null;
    }
  }

  /**
   * Check if Airtable is configured
   */
  isConfigured() {
    return !!this.base;
  }

  /**
   * Get the Airtable base instance (for direct operations)
   */
  getBase() {
    return this.base;
  }

  /**
   * Create a new content file record in Airtable
   * @param {Object} fileData - File metadata
   * @returns {Promise<Object>} Result with success status and record data
   */
  async createContentFile(fileData) {
    const startTime = Date.now();

    logger.info('Creating content file record', {
      fileName: fileData.fileName,
      fileHash: fileData.fileHash,
      fileSize: fileData.fileSize
    });

    try {
      if (!this.base) {
        logger.warn('Airtable not configured, skipping metadata storage');
        return {
          success: false,
          error: 'Airtable not configured',
          skipped: true
        };
      }

      // Prepare fields for Airtable
      const fields = {
        'File Name': fileData.fileName,
        'File Hash': fileData.fileHash,
        'Blob URL': fileData.blobUrl,
        'File Type': fileData.fileType || 'unknown',
        'MIME Type': fileData.mimeType || 'application/octet-stream',
        'File Size (bytes)': fileData.fileSize || 0,
        'Access Level': fileData.accessLevel || 'Authenticated',
        'Status': fileData.status || 'Active',
        'Upload Date': new Date().toISOString().split('T')[0]
      };

      // Add optional fields if provided
      if (fileData.fileDescription) fields['File Description'] = fileData.fileDescription;
      if (fileData.originalUrl) fields['Original URL'] = fileData.originalUrl;
      if (fileData.tags && fileData.tags.length > 0) fields['Tags'] = fileData.tags;
      if (fileData.projectName) fields['Project Name'] = fileData.projectName;
      if (fileData.notes) fields['Notes'] = fileData.notes;
      if (fileData.thumbnailUrl) fields['Thumbnail URL'] = fileData.thumbnailUrl;

      logger.info('Sending create request to Airtable', { fieldsCount: Object.keys(fields).length });

      // Create record in Airtable
      const records = await this.base('Content Files').create([{ fields }]);
      const record = records[0];

      const duration = Date.now() - startTime;

      logger.success('Content file record created', {
        recordId: record.id,
        fileName: record.get('File Name'),
        fileHash: record.get('File Hash'),
        duration: `${duration}ms`
      });

      return {
        success: true,
        data: {
          id: record.id,
          fileName: record.get('File Name'),
          fileHash: record.get('File Hash'),
          blobUrl: record.get('Blob URL'),
          fileType: record.get('File Type'),
          mimeType: record.get('MIME Type'),
          fileSize: record.get('File Size (bytes)'),
          accessLevel: record.get('Access Level'),
          status: record.get('Status'),
          uploadDate: record.get('Upload Date'),
          createdDate: record.createdTime
        },
        duration: `${duration}ms`
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      logger.error('Failed to create content file record', {
        ...error,
        fileName: fileData.fileName,
        duration: `${duration}ms`
      });

      return {
        success: false,
        error: error.message,
        details: error.toString()
      };
    }
  }

  /**
   * Get content file by hash
   * @param {string} fileHash - SHA-256 hash of the file
   * @returns {Promise<Object>} Result with file data
   */
  async getContentFileByHash(fileHash) {
    const startTime = Date.now();

    logger.info('Fetching content file by hash', { fileHash });

    try {
      if (!this.base) {
        return {
          success: false,
          error: 'Airtable not configured'
        };
      }

      const records = await this.base('Content Files').select({
        filterByFormula: `{File Hash} = "${fileHash}"`,
        maxRecords: 1
      }).all();

      const duration = Date.now() - startTime;

      if (records.length === 0) {
        logger.warn('Content file not found', { fileHash, duration: `${duration}ms` });
        return {
          success: false,
          error: 'File not found'
        };
      }

      const record = records[0];

      logger.success('Content file found', {
        recordId: record.id,
        fileName: record.get('File Name'),
        duration: `${duration}ms`
      });

      return {
        success: true,
        data: {
          id: record.id,
          fileName: record.get('File Name'),
          fileHash: record.get('File Hash'),
          blobUrl: record.get('Blob URL'),
          fileType: record.get('File Type'),
          mimeType: record.get('MIME Type'),
          fileSize: record.get('File Size (bytes)'),
          accessLevel: record.get('Access Level'),
          status: record.get('Status'),
          uploadDate: record.get('Upload Date'),
          createdDate: record.createdTime
        }
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      logger.error('Failed to fetch content file', {
        ...error,
        fileHash,
        duration: `${duration}ms`
      });

      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get all content files (with optional filters)
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Result with files array
   */
  async getContentFiles(options = {}) {
    const startTime = Date.now();

    logger.info('Fetching content files', options);

    try {
      if (!this.base) {
        return {
          success: false,
          error: 'Airtable not configured'
        };
      }

      const selectOptions = {
        maxRecords: options.limit || 100,
        sort: [{ field: options.sortBy || 'Upload Date', direction: options.sortOrder || 'desc' }]
      };

      if (options.filterFormula) {
        selectOptions.filterByFormula = options.filterFormula;
      }

      const records = await this.base('Content Files').select(selectOptions).all();

      const duration = Date.now() - startTime;

      const files = records.map(record => ({
        id: record.id,
        fileName: record.get('File Name'),
        fileHash: record.get('File Hash'),
        blobUrl: record.get('Blob URL'),
        fileType: record.get('File Type'),
        mimeType: record.get('MIME Type'),
        fileSize: record.get('File Size (bytes)'),
        accessLevel: record.get('Access Level'),
        status: record.get('Status'),
        uploadDate: record.get('Upload Date'),
        createdDate: record.createdTime
      }));

      logger.success('Content files fetched', {
        count: files.length,
        duration: `${duration}ms`
      });

      return {
        success: true,
        data: files,
        count: files.length
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      logger.error('Failed to fetch content files', {
        ...error,
        duration: `${duration}ms`
      });

      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Authenticate user against Users table
   * @param {string} username - Username
   * @param {string} password - Password
   * @returns {Promise<Object>} Result with user data
   */
  async authenticateUser(username, password) {
    const startTime = Date.now();

    logger.info('Authenticating user', { username });

    try {
      if (!this.base) {
        return {
          success: false,
          error: 'Airtable not configured'
        };
      }

      const records = await this.base('Users').select({
        filterByFormula: `AND({Username} = "${username}", {Active Status} = TRUE())`,
        maxRecords: 1
      }).all();

      if (records.length === 0) {
        logger.warn('User not found or inactive', { username });
        return {
          success: false,
          error: 'Invalid credentials'
        };
      }

      const record = records[0];
      const storedPassword = record.get('Access Passcode');

      if (storedPassword !== password) {
        logger.warn('Password mismatch', { username });
        return {
          success: false,
          error: 'Invalid credentials'
        };
      }

      const duration = Date.now() - startTime;

      // Update last login
      try {
        await this.base('Users').update([{
          id: record.id,
          fields: { 'Last Login': new Date().toISOString() }
        }]);
      } catch (updateError) {
        logger.warn('Failed to update last login', { error: updateError.message });
      }

      logger.success('User authenticated successfully', {
        username,
        duration: `${duration}ms`
      });

      return {
        success: true,
        data: {
          id: record.id,
          email: record.get('Email'),
          firstName: record.get('First Name'),
          lastName: record.get('Last Name'),
          username: record.get('Username'),
          userRole: record.get('User Role'),
          activeStatus: record.get('Active Status')
        }
      };
    } catch (error) {
      const duration = Date.now() - startTime;
      logger.error('Authentication failed', {
        ...error,
        username,
        duration: `${duration}ms`
      });

      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Export singleton instance
const airtableService = new AirtableService();
export default airtableService;

// Named exports
export { AirtableService };
